import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import DataItem from 'Views/Onboarding/Components/ContextDataDisplay/DataItem';
import CallbackButton from 'Views/Onboarding/Components/ContextDataDisplay/CallbackButton';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { useDebugState } from 'Context/DebugContext';
import { getBorder, getBorderRadius } from 'Components/utils';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';

const styles = theme => ({
  root: {
    padding: theme.spacing(1),
    ...getBorderRadius(theme.radius.md, { all: true }),
    ...getBorder(theme, { all: true }),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.common.white,
    width: '100%',
  },
  header: {
    padding: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  contentHidden: {
    display: 'none',
  },
});

const ContextDataDisplay = ({
  className,
  classes,
  title,
  contextValue,
  amendmentMap,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const { showDebugContextData } = useDebugState();

  const { values, functions } = Object.entries(contextValue).reduce((acc, curr) => {
    const [, v] = curr;

    if (typeof v === 'function') {
      return {
        ...acc,
        functions: [...acc.functions, curr].sort((a, b) => { return a[0] - b[0]; }),
      };
    }

    return {
      ...acc,
      values: [...acc.values, curr].sort((a, b) => { return a[0] - b[0]; }),
    };
  }, { values: [], functions: [] });

  if (!showDebugContextData) {
    return null;
  }

  const filledAmendmentMap = Object.entries(amendmentMap).reduce((acc, curr) => {
    const [k, v] = curr;

    acc[k] = v(contextValue);

    return acc;
  }, {});

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <Box className={classes.header}>
        <Typography
          weight={'bold'}
          variant={'h4'}
        >
          {title}
        </Typography>
        <Button
          color={'accentBlue'}
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </Box>
      <Box className={classNames(classes.content, {
        [classes.contentHidden]: !expanded,
      })}
      >
        <Typography
          className={classes.sectionHeader}
          variant={'h5'}
        >
          {'Values'}
        </Typography>
        {values.map((item) => {
          const [k, v] = item;

          return (
            <DataItem
              key={k}
              label={k}
              value={v}
              {...filledAmendmentMap[k]}
            />
          );
        })}
        <Typography
          className={classes.sectionHeader}
          variant={'h3'}
        >
          {'Functions'}
        </Typography>
        {functions.map((item) => {
          const [k, v] = item;

          return (
            <CallbackButton
              key={k}
              label={k}
              onClick={v}
              {...filledAmendmentMap[k]}
            />
          );
        })}
      </Box>
    </Box>
  );
};

ContextDataDisplay.defaultProps = {
  className: '',
  amendmentMap: {},
};

ContextDataDisplay.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  contextValue: PropTypes.object.isRequired,
  amendmentMap: PropTypes.object,
};

export default withStyles(styles)(ContextDataDisplay);
